import { ref, defineComponent, provide, reactive, onMounted, onUnmounted, nextTick, watch, computed, } from 'vue';
import _ from 'lodash';
import useConfigInject from '../../_util/hooks/useConfigInject';
import { barlineChartContextKey } from './constants';
import { getColors } from '../../_util/chartsColors';
import XAxis from './xAxis';
import Legend from './legend';
import Spin from '../../spin';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';
import { combineTooltipFormatter } from './computed';
import { SeriesType } from './const';
import { hexToRgba } from '../../utils/common';
import { numberFormat, bigNumberUnit } from 'wetrade-tools';
import BigNumber from 'bignumber.js';
export const QuotationColorMap = {
    redUp: 0,
    greenUp: 1,
};
// 注册必须的组件
echarts.use([
    TooltipComponent,
    GridComponent,
    LegendComponent,
    BarChart,
    LineChart,
    CanvasRenderer,
    SVGRenderer,
]);
export const BarLineChartProps = () => ({
    quotationColor: {
        type: Number,
        default: 0,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    chartData: {
        type: Object,
        default: () => {
            return {
                x: [],
                data: [],
            };
        },
    },
    grid: {
        type: Array,
        default: () => [],
    },
    unit: {
        type: [String, Array],
        default: '',
    },
    colors: {
        type: Array,
        default: () => [],
    },
    height: {
        type: String,
        default: '300px',
    },
    scene: {
        type: [String, Array],
        default: 'bar',
    },
    isStack: {
        type: Boolean,
        default: false,
    },
    stack: {
        type: Array,
        default: () => [],
    },
    isArea: {
        type: Boolean,
        default: false,
    },
    top: {
        type: Number,
        default: 50,
    },
    right: {
        type: [Number, String],
        default: 4,
    },
    left: {
        type: Number,
        default: 28,
    },
    bottom: {
        type: Number,
        default: 8,
    },
    barWidth: {
        type: [String, Number],
        default: '20%',
    },
    // 柱子的最大宽度
    barMaxWidth: {
        type: [String, Number],
        default: 32,
    },
    // 柱条的最小宽度
    barMinWidth: {
        type: [String, Number],
        default: 18,
    },
    // 柱条之间的距离 数值为百分比 相对于柱条的宽度
    barGap: {
        type: String,
        default: '20%',
    },
    cursor: {
        type: String,
        default: 'pointer',
    },
    showLegend: {
        type: Boolean,
        default: false,
    },
    // 图例
    legend: {
        type: Array,
        default: () => [],
    },
    // 图例样式
    legendPosition: {
        type: Object,
        default: () => {
            return {
                bottom: 20,
            };
        },
    },
    showXAxis: {
        type: Boolean,
        default: false,
    },
    tooltipAxisPointerType: {
        type: String,
        default: 'shadow',
    },
    tooltipAxisPointerLineType: {
        type: String,
        default: 'solid',
    },
    // 图例是否支持取消选择
    selectedMode: {
        type: Boolean,
        default: false,
    },
    // 图例选中状态表，动态配置图例显示项，适用于 selectedMode = true 时
    selectedModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    xAxis: {
        type: Array,
        default: () => [],
    },
    showXAxisLabel: {
        type: Boolean,
        default: true,
    },
    xLabelInterval: {
        type: [String, Number],
        default: 'auto',
    },
    xLabelSize: {
        type: Number,
        default: 12,
    },
    // x轴文字块的内边距, 例：[3, 4, 5, 6]、4
    xLabelPadding: {
        type: [Array, Number],
        default: 0,
    },
    xAxisBoundaryGap: {
        type: [Boolean, Array],
        default: true,
    },
    xLabelAlign: {
        type: String,
        default: 'center',
    },
    // 是否显示Y轴
    showYAxis: {
        type: Boolean,
        default: true,
    },
    // 是否显示Y轴label
    showYAxisLabel: {
        type: Boolean,
        default: true,
    },
    // y轴配置
    yAxis: {
        type: Array,
        default: () => [],
    },
    // 显示总数label 仅在bar堆叠场景下有效
    showTotalLabel: {
        type: [Boolean, Array],
        default: false,
    },
    showYAxisSplitLine: {
        type: Boolean,
        default: true,
    },
    // 显示千分位符号 on:打开 off:关闭，如果是单个字符串则全部开 or 关，如果是数组则根据对应的数组顺序去开 or 关 只用于 tooltip
    thousandthSign: {
        type: [Array, String],
        default: 'off',
    },
    // 小数点位数 单个数值则全部应用，为数组则按顺序应用 只用于 legend 和 tooltip
    pointerNum: {
        type: [Number, Array],
        default: 2,
    },
    showTooltip: {
        type: Boolean,
        default: true,
    },
    toolTipPatchFunc: {
        default: null,
    },
    // 数据对应 y 轴的下标，仅在 yAxis 长度大于 1 时有效,必须与 yAxis 对应，否则会异常
    yAxisIndex: {
        type: [Number, Array],
        default: 0,
    },
    yAxisUnit: {
        type: [String, Array],
        default: '',
    },
    yAxisPointerNum: {
        type: [Number, Array],
        default: 0,
    },
    // y 轴千分位
    yAxisthousandthSign: {
        type: [String, Array],
        default: 'on',
    },
    // x 轴 label 是否换行
    xLabelWrap: {
        type: Boolean,
        default: false,
    },
    xLabelLineFontNum: {
        type: Number,
        default: 5,
    },
    // 显示默认数值 这样会保存默认小数位
    showDefaultNum: {
        type: Boolean,
        default: false,
    },
    // tooltip 的位置是否支持自定义 true 重新计算 tooltip 的位置
    tooltipControlPos: {
        type: Boolean,
        default: false,
    },
    // 计算 tooltip 位置的函数
    computedPosition: {
        type: Function,
        default: () => {
            return null;
        },
    },
    // Y 轴的数值是否需要换算，单位有万，亿，万亿，默认不换算
    isYAxisConversion: {
        type: [Boolean, Array],
        default: false,
    },
    // y轴数值转换后的小数位显示规则 true 按有效小数位显示 false 按yAxisPointerNum值显示
    yAxisConversionDecimal: {
        type: Boolean,
        default: false,
    },
    // tooltip的数值是否需要换算，单位有万，亿，万亿，默认不换算
    isTooltipConversion: {
        type: [Boolean, Array],
        default: false,
    },
    // 柱条最小高度，可用于防止某数据项的值过小而影响交互
    // 设置了最小高度，data 传值时，不需要最小高度的值要传 null，传 0 会展示最小高度，null 不会
    barMinHeight: {
        type: Number,
        default: 0,
    },
    // tooltip 头部文字是否显示 头部为日期或文案，是否需要显示等
    showTipHeaderText: {
        type: Boolean,
        default: true,
    },
    // tooltip底部显示的文字label内容
    bottomText: {
        type: String,
        default: '',
    },
    // tooltip中值是否需要向下取整
    roundDown: {
        type: Boolean,
        default: false,
    },
    // tooltip底部显示的内容的值集合
    bottomValueList: {
        type: Array,
        default: () => [],
    },
    // 为 null 的数据是否绘制为 0，图表绘制为0，图例显示为--
    isNullShow: {
        type: Boolean,
        default: false,
    },
    // 是否连接null
    isConnectedNull: {
        type: Boolean,
        default: false,
    },
    // label是否要删除[]及内容, 使用于tooltip；场景：selectedModel为true时，需要增加唯一标识确定不同的label，显示时需删除
    isLabelRemoveId: {
        type: Boolean,
        default: false,
    },
    // tooltip formatter
    combineTooltipFormatter: { default: () => combineTooltipFormatter },
    showSymbol: {
        type: Boolean,
        default: false,
    },
    symbolType: {
        type: String,
        default: 'circle',
    },
    // tooltip是否直接展示字符串
    showStringValue: {
        type: Boolean,
        default: false,
    },
    // 图例 - 数据
    legendList: {
        type: Array,
        default: () => [],
    },
    // 图例 - 大于1行时，是否显示左右箭头
    isShowTotal: {
        type: Boolean,
        default: false,
    },
    /** 图例 - 边距
     * in 图例与文字间的间距
     * out 图例与图例间的间距
     * */
    spacing: {
        type: Object,
        default: () => {
            return {
                in: 10,
                out: 24,
            };
        },
    },
    showCustomX: {
        type: Boolean,
        default: true,
    },
    showCustomXAll: {
        type: Boolean,
        default: false,
    },
    showCustomLegend: {
        type: Boolean,
        default: true,
    },
    lineStyleWidth: {
        type: Number,
        default: 1,
    },
    // 当前下标线条是实线还是虚线，仅对scene=line有效
    seriesLine: {
        type: Array,
        default: () => [],
    },
    confine: {
        type: Boolean,
        default: false,
    },
    xLabelOverflow: {
        type: Boolean,
        default: false,
    },
    totalLabelColor: {
        type: [String, Array],
        default: '#165DFF',
    },
    isTooltipLabelShow: {
        type: Boolean,
        default: false,
    },
    isMarkLine: {
        type: Boolean,
        default: false,
    },
    mouseOutView: {
        type: Function,
        default: () => ({}),
    },
    renderer: {
        type: String,
        default: 'svg',
    },
    isNumberDefault: {
        type: Boolean,
        default: false,
    },
    // 供账目分析-账目差值、银行等数据场景用, tooltip的label是否要取chart.x里的值
    showTooltipCurName: {
        type: Boolean,
        default: false,
    },
    customXAxis: {
        type: Array,
        default: () => [],
    },
    customSeries: {
        type: Array,
        default: () => [],
    },
    emphasisFocus: {
        type: Boolean,
        default: false,
    },
    areaOpacity: {
        type: Number,
        default: 0.1,
    },
    areaColorOpacity: {
        type: Number,
        default: 1,
    },
    isSmooth: {
        type: [Boolean, Number],
        default: false,
    },
    endLabel: {
        type: Function,
        default: () => ({}),
    },
});
const BarLineChart = defineComponent({
    name: 'WdBarLineChart',
    XAxis,
    Legend,
    props: BarLineChartProps(),
    slots: ['mark'],
    setup(props, { emit, expose, slots }) {
        const { prefixCls, configProvider } = useConfigInject('barLineChart', props);
        // 如果X轴为自定义的对象数组，图表绘制时取text字段；如果X轴为字符串数组，直接用；
        const xAxisArr = computed(() => {
            return props.chartData.x.map(t => t?.realText || t?.text || t);
        });
        const wrapRef = ref();
        const mainRef = ref();
        let myChart = null;
        const resizeOb = ref(null);
        // 原始x轴label是否换行处理, 遍历字符串数组，按需要求余
        const handleXLabel = val => {
            const fontNum = props.xLabelLineFontNum;
            let str = '';
            for (let i = 0; i < val.length; i++) {
                str += val[i];
                if (!((i + 1) % fontNum))
                    str += '\n';
            }
            return str;
        };
        // 适配
        const handlerResize = () => {
            myChart?.resize();
        };
        // 鼠标移出图表后节流执行mouseOutView，举例：投管-持有资产-收益走势图
        const handlerMouseOutView = _.throttle(() => {
            props.mouseOutView();
        }, 300);
        // 初始化系列数据
        const getSeries = () => {
            let series = [];
            const legend = props.legend;
            // 指定图表的配置项和数据, 防止名称一样颜色会自动渲染成一致
            const colors = getColors(configProvider?.mode, props.quotationColor);
            const nColor = props.colors.length > 0 ? props.colors : colors.COMMONCOLOR;
            // 如果为自定义图表，在此生成series，主要用于隐藏部分不需要显示的柱状占位的问题 举例：账目汇总-美股-交易概览-费率对比
            if (props.customSeries?.length) {
                series = _.cloneDeep(props.customSeries);
                for (let i = 0, length = series?.length; i < length; i++) {
                    Object.assign(series[i], {
                        barWidth: props.barWidth,
                        barMaxWidth: props.barMaxWidth,
                        barMinWidth: props.barMinWidth,
                        barMinHeight: props.barMinHeight,
                        barGap: props.barGap,
                        type: props.scene,
                    });
                }
                return series;
            }
            // 其余柱状图
            for (let i = 0, length = props.chartData?.data.length; i < length; i++) {
                let data = props.chartData.data[i];
                // 如果null需要绘制为0, 需要把data转为0
                if (props.isNullShow) {
                    data = data.map(t => (!t ? 0 : t));
                }
                const nowColor = nColor[i % nColor.length];
                const dataScene = typeof props.scene === 'string' ? props.scene : props.scene[i];
                series[i] = {
                    name: legend[i],
                    type: dataScene,
                    data,
                    color: nowColor,
                    connectNulls: props.isConnectedNull,
                    itemStyle: {
                        color: nowColor,
                    },
                    emphasis: {
                        focus: props.emphasisFocus ? 'series' : false,
                    },
                    smooth: props.isSmooth,
                };
                // 根据不同的场景类型添加不同的配置
                if (dataScene === SeriesType.BAR) {
                    Object.assign(series[i], {
                        barWidth: props.barWidth,
                        barMaxWidth: props.barMaxWidth,
                        barMinWidth: props.barMinWidth,
                        barMinHeight: props.barMinHeight,
                        barGap: props.barGap,
                        cursor: props?.cursor || 'pointer',
                    });
                    props.isStack &&
                        Object.assign(series[i], { stack: props.stack.length ? props.stack[i] : 'total' });
                }
                if (dataScene === SeriesType.LINE) {
                    const lineType = props.seriesLine.length ? props.seriesLine[i] : 'solid';
                    Object.assign(series[i], {
                        showSymbol: (xAxisArr.value.length > 1 ? false : true) || props.showSymbol,
                        symbol: props.symbolType,
                        symbolSize: 7,
                        lineStyle: {
                            width: props.lineStyleWidth,
                            type: lineType,
                            opacity: props.isMarkLine ? 0 : 1,
                        },
                        itemStyle: {
                            borderWidth: 2,
                            borderColor: colors.SYMBOL_BORDER_COLOR,
                        },
                        markLine: {
                            silent: true,
                            symbol: 'none',
                            data: [],
                        },
                    });
                    if (props.isMarkLine && props.chartData?.data.length > 1) {
                        series[i].markLine.data.push({
                            name: legend[i],
                            yAxis: props.chartData.data[i][0],
                            lineStyle: {
                                color: nowColor,
                                type: 'solid',
                                opacity: props.isMarkLine ? 1 : 0,
                            },
                            label: {
                                show: false,
                            },
                        });
                    }
                    // 面积图增加渐变
                    props.isArea &&
                        Object.assign(series[i], {
                            areaStyle: {
                                opacity: props.areaOpacity,
                                color: props.areaColorOpacity || props.areaColorOpacity == 0
                                    ? new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                        {
                                            offset: 0,
                                            color: nowColor,
                                        },
                                        {
                                            offset: 1,
                                            color: hexToRgba(nowColor, props.areaColorOpacity),
                                        },
                                    ])
                                    : null, // 背景颜色透明度 最深色 ~ 最浅色的比例配置
                            },
                        });
                    // stack 根据是否配置显示
                    props.isStack &&
                        Object.assign(series[i], { stack: props.stack.length ? props.stack[i] : 'total' });
                }
                const yAxisIndex = typeof props.yAxisIndex === 'object' &&
                    props.yAxisIndex?.length > 0 &&
                    typeof props.yAxisIndex !== 'number'
                    ? props.yAxisIndex[i]
                    : props.yAxisIndex;
                if (props.yAxis.length > 0) {
                    Object.assign(series[i], { yAxisIndex });
                }
            }
            return series;
        };
        const handleSeries = () => {
            const series = getSeries();
            series.map((item, index) => Object.assign(item, {
                barMinHeight: props.barMinHeight,
                label: {
                    show: typeof props.showTotalLabel === 'boolean'
                        ? props.showTotalLabel
                        : props.showTotalLabel[index],
                    fontSize: 12,
                    position: 'top',
                    color: typeof props.totalLabelColor === 'string'
                        ? props.totalLabelColor
                        : props.totalLabelColor[index] || '#165DFF',
                    formatter: params => {
                        const unit = props.unit.length > 0 && typeof props.unit !== 'string'
                            ? props.unit[index]
                            : props.unit;
                        // 是否显示小数位
                        const pointerNum = (typeof props.pointerNum !== 'number' ? props.pointerNum[index] : props.pointerNum);
                        // 是否显示千位符
                        const symbol = props.thousandthSign.length > 0 && typeof props.thousandthSign !== 'string'
                            ? props.thousandthSign[index]
                            : props.thousandthSign;
                        // 是否要转换为万、亿
                        const isConversion = typeof props.isTooltipConversion === 'boolean'
                            ? props.isTooltipConversion
                            : props.isTooltipConversion[index];
                        // 跟随tooltip是否换算规则
                        let value = params.value;
                        value = numberFormat(value, {
                            decimal: pointerNum,
                            t: symbol === 'on',
                            r: isConversion
                                ? BigNumber.ROUND_HALF_UP
                                : props.roundDown
                                    ? BigNumber.ROUND_DOWN
                                    : BigNumber.ROUND_HALF_UP,
                            os: isConversion,
                        });
                        return value + unit;
                    },
                },
            }));
            return series;
        };
        const init = () => {
            if (myChart) {
                // 移除所有ECharts事件监听
                myChart.off('updateAxisPointer');
                myChart.getZr().off('globalout', handlerMouseOutView);
                myChart.off('mouseover');
                myChart.off('mouseout');
                myChart.clear();
                myChart.dispose();
                myChart = null;
            }
            if (!mainRef.value)
                return;
            myChart = echarts.init(mainRef.value, null, {
                renderer: props.renderer,
            });
            const colors = getColors(configProvider?.mode, props.quotationColor);
            const colorList = props.colors.length > 0 ? props.colors : colors.COMMONCOLOR;
            const shadowParams = props.customSeries?.length ? {} : { color: colors.POINTER_SHADOW };
            // 指定图表的配置项和数据
            const option = {
                color: colorList,
                grid: props.grid.length
                    ? props.grid
                    : {
                        top: props.top,
                        left: props.left,
                        right: props.right,
                        bottom: props.bottom,
                    },
                tooltip: {
                    show: props.showTooltip,
                    trigger: 'axis',
                    transitionDuration: 0,
                    confine: props.confine,
                    axisPointer: {
                        // 坐标轴指示器，坐标轴触发有效
                        type: props.customSeries?.length ? 'none' : props.tooltipAxisPointerType,
                        lineStyle: {
                            color: colors.POINTER_LINE,
                            type: props.tooltipAxisPointerLineType,
                        },
                        shadowStyle: {
                            ...shadowParams,
                        },
                        label: {
                            show: props.isTooltipLabelShow,
                            backgroundColor: colors.BAR_TOOLTIP_LABEL_COLOR,
                            color: '#fff',
                            padding: 5,
                            fontWeight: 600,
                            fontSize: 10,
                            margin: 5,
                            formatter: e => {
                                return `${e.value}`;
                            },
                        },
                        z: 0,
                    },
                    padding: props.showTipHeaderText ? 12 : [2, 12, 12, 12],
                    backgroundColor: colors.TOOLTIP_BG,
                    borderColor: colors.TOOLTIP_BDBGC,
                    textStyle: {
                        color: colors.TEXT,
                        fontSize: 12,
                        fontWeight: 600,
                    },
                    extraCssText: `box-shadow: ${colors.NEW_TREND_TOOLTIP_SHADOW};`,
                    formatter: params => {
                        return props.combineTooltipFormatter({
                            params,
                            props,
                        });
                    },
                    position: (point, params, dom, rect, size) => {
                        // tooltip太多时，是否换行处理 解决超出浏览器看不到tooltip的问题
                        return props.computedPosition(point, params, dom, rect, size);
                    },
                },
                legend: {
                    show: props.showLegend,
                    ...props.legendPosition,
                    itemWidth: props.scene === SeriesType.LINE ? 10 : 8,
                    itemHeight: props.scene === SeriesType.LINE ? 3 : 8,
                    itemGap: 24,
                    selectedMode: props.selectedMode,
                    padding: [0, 20, 0, 0],
                    textStyle: {
                        fontStyle: '12px',
                        color: colors.TEXT,
                        lineHeight: 22,
                    },
                    itemStyle: {
                        borderWidth: 0,
                    },
                    data: props.legend,
                },
                textStyle: {
                    color: colors.TEXT,
                },
                xAxis: handleXAxis(),
                yAxis: handleYAxis(),
                series: props.showTotalLabel ? handleSeries() : getSeries(),
            };
            myChart.on('updateAxisPointer', (event) => {
                const xAxisInfo = event?.axesInfo[0];
                if (xAxisInfo) {
                    const dimension = xAxisInfo.value + 1;
                    emit('handleUpdateAxisPointer', dimension);
                }
            });
            // 使用指定的配置项和数据显示图表。
            myChart.setOption(option);
            myChart.getZr().on('globalout', handlerMouseOutView);
            if (!props.emphasisFocus || !props.customSeries?.length)
                return;
            // 鼠标移入柱子高亮，其他透明度变低
            const allColor = [];
            myChart.on('mouseover', e => {
                // 遍历补齐所有颜色，避免颜色循环时相互影响
                let findObj = undefined;
                for (let i = 0, len = props.customSeries?.length; i < len; i++) {
                    allColor.push(colorList[i % colorList.length]);
                    if (!findObj && props.customSeries[i]?.name === e.seriesName) {
                        findObj = props.customSeries[i];
                    }
                }
                const color = [];
                allColor.forEach((t, i) => {
                    color.push(i !== findObj?.selfIndex ? hexToRgba(t) : t);
                });
                option.color = color;
                myChart?.setOption(option);
            });
            myChart.on('mouseout', () => {
                option.color = allColor;
                myChart?.setOption(option);
            });
        };
        const handleXAxis = () => {
            const colors = getColors(configProvider?.mode, props.quotationColor);
            const xAxis = [];
            const defaultXAxisItem = () => {
                {
                    return {
                        data: xAxisArr.value,
                        show: props.showXAxis,
                        boundaryGap: props.xAxisBoundaryGap,
                        axisTick: {
                            // 刻度
                            show: false,
                        },
                        axisLine: {
                            lineStyle: {
                                color: colors.AXISLINE,
                                type: 'dashed',
                            },
                        },
                        axisLabel: {
                            show: props.showXAxisLabel,
                            fontFamily: 'custom-fonts',
                            interval: props.xLabelInterval,
                            align: props.xLabelAlign,
                            showMinLabel: true,
                            showMaxLabel: true,
                            fontSize: props.xLabelSize,
                            lineHeight: 16,
                            padding: props.xLabelPadding,
                            formatter: value => {
                                // 是否需要换行
                                if (props.xLabelWrap) {
                                    return handleXLabel(value);
                                }
                                return value;
                            },
                        },
                        splitLine: {
                            lineStyle: {
                                color: colors.AXISLINE,
                            },
                        },
                    };
                }
            };
            const curXAxis = props.customXAxis?.length ? props.customXAxis : props.xAxis;
            if (curXAxis?.length > 0) {
                for (let i = 0; i < curXAxis.length; i++) {
                    const defaultItem = defaultXAxisItem();
                    const shadowParams = props.customSeries?.length ? { color: colors.POINTER_SHADOW } : {};
                    const item = _.cloneDeep(defaultItem);
                    if (props.customSeries?.length && !i) {
                        item.axisPointer = {
                            type: props.tooltipAxisPointerType,
                            shadowStyle: {
                                ...shadowParams,
                            },
                        };
                    }
                    Object.assign(item, props.xAxis[i]);
                    xAxis.push(item);
                }
                return xAxis;
            }
            return defaultXAxisItem();
        };
        // Y轴处理
        const handleYAxis = () => {
            const colors = getColors(configProvider?.mode, props.quotationColor);
            const yAxis = [];
            // 默认yAxis数据
            const defaultYAxisItem = (unit = '', // 单位
            pointerNum, // y 轴小数位
            symbol, // y 轴千分位
            isConversion) => {
                return {
                    show: props.showYAxis,
                    axisLabel: {
                        color: colors.CHART_TICK,
                        fontFamily: 'custom-fonts',
                        show: props.showYAxisLabel,
                        formatter: (value) => {
                            let val = value;
                            if (isConversion) {
                                const conversion = bigNumberUnit({
                                    value: val,
                                    isConversion: true,
                                    decimal: props.yAxisConversionDecimal ? undefined : pointerNum,
                                });
                                return conversion?.value + conversion?.unit + unit;
                            }
                            if (!props.showDefaultNum) {
                                val = numberFormat(value, {
                                    decimal: pointerNum,
                                    t: symbol === 'on',
                                });
                            }
                            return val + unit;
                        },
                    },
                    splitLine: {
                        show: props.showYAxisSplitLine,
                        lineStyle: {
                            type: 'dashed',
                            color: colors.AXISLINE,
                        },
                    },
                    axisLine: {
                        show: false, // y轴线隐藏
                    },
                    axisTick: {
                        show: false, // 刻度隐藏
                    },
                };
            };
            if (props.yAxis.length > 0) {
                for (let i = 0; i < props.yAxis.length; i++) {
                    const unit = typeof props.yAxisUnit !== 'string' ? props.yAxisUnit[i] : props.yAxisUnit;
                    const pointerNum = props.yAxisPointerNum.length > 0 && typeof props.yAxisPointerNum !== 'number'
                        ? props.yAxisPointerNum[i]
                        : props.yAxisPointerNum;
                    const symbol = props.yAxisthousandthSign.length > 0 && typeof props.yAxisthousandthSign !== 'string'
                        ? props.yAxisthousandthSign[i]
                        : props.yAxisthousandthSign;
                    const isConversion = typeof props.isYAxisConversion === 'boolean'
                        ? props.isYAxisConversion
                        : props.isYAxisConversion[i];
                    const defaultItem = defaultYAxisItem(unit, pointerNum, symbol, isConversion);
                    const item = _.cloneDeep(defaultItem);
                    Object.assign(item, props.yAxis[i]);
                    yAxis.push(item);
                }
                return yAxis;
            }
            return defaultYAxisItem();
        };
        // 更新数据
        const updateData = () => {
            if (!myChart)
                return;
            const colors = getColors(configProvider?.mode, props.quotationColor);
            const shadowParams = props.customSeries?.length ? {} : { color: colors.POINTER_SHADOW };
            const option = {
                grid: props.grid.length
                    ? props.grid
                    : {
                        top: props.top,
                        left: props.left,
                        right: props.right,
                        bottom: props.bottom,
                    },
                tooltip: {
                    axisPointer: {
                        // 坐标轴指示器，坐标轴触发有效
                        type: props.customSeries?.length ? 'none' : props.tooltipAxisPointerType,
                        lineStyle: {
                            color: colors.POINTER_LINE,
                            type: props.tooltipAxisPointerLineType,
                        },
                        shadowStyle: {
                            ...shadowParams,
                        },
                        label: {
                            show: props.isTooltipLabelShow,
                            backgroundColor: colors.BAR_TOOLTIP_LABEL_COLOR,
                            color: '#fff',
                            padding: 5,
                            fontWeight: 600,
                            fontSize: 10,
                            margin: 5,
                            formatter: e => {
                                return `${e.value}`;
                            },
                        },
                        z: 0,
                    },
                },
                legend: {
                    data: props.legend,
                    selected: props.selectedModel,
                },
                xAxis: {
                    data: xAxisArr.value,
                    axisLabel: {
                        interval: props.xLabelInterval,
                        formatter: value => {
                            // 是否需要换行
                            if (props.xLabelWrap) {
                                return handleXLabel(value);
                            }
                            return value;
                        },
                    },
                },
                yAxis: handleYAxis(),
                series: props.showTotalLabel ? handleSeries() : getSeries(),
            };
            myChart.setOption(option);
            handlerResize();
        };
        // 更新Y轴颜色
        const updateYAxisColor = () => {
            const colors = getColors(configProvider?.mode, props.quotationColor);
            const defaultYAxis = {
                axisLabel: {
                    color: colors.CHART_TICK,
                },
                splitLine: {
                    lineStyle: {
                        color: colors.AXISLINE,
                    },
                },
            };
            const yAxis = [];
            if (props.yAxis.length > 0) {
                for (let i = 0; i < props.yAxis.length; i++) {
                    yAxis.push(defaultYAxis);
                }
                return yAxis;
            }
            return defaultYAxis;
        };
        // 更新主题色
        const updateColor = () => {
            if (!myChart)
                return;
            const colors = getColors(configProvider?.mode, props.quotationColor);
            const yAxis = updateYAxisColor();
            const xAxis = handleXAxis();
            const shadowParams = props.customSeries?.length ? {} : { color: colors.POINTER_SHADOW };
            const option = {
                color: props.colors.length > 0 ? props.colors : colors.COMMONCOLOR,
                legend: {
                    textStyle: {
                        color: colors.TEXT,
                    },
                },
                textStyle: {
                    color: colors.TEXT,
                },
                tooltip: {
                    backgroundColor: colors.TOOLTIP_BG,
                    borderColor: colors.TOOLTIP_BDBGC,
                    axisPointer: {
                        lineStyle: {
                            color: colors.POINTER_LINE,
                        },
                        shadowStyle: {
                            ...shadowParams,
                        },
                    },
                    textStyle: {
                        color: colors.TEXT,
                    },
                    extraCssText: `box-shadow: ${colors.NEW_TREND_TOOLTIP_SHADOW};`,
                },
                xAxis,
                yAxis,
                series: props.showTotalLabel ? handleSeries() : getSeries(),
            };
            myChart.setOption(option);
        };
        // 更新图例
        const updateSelected = () => {
            const option = {
                animation: false,
                legend: {
                    selected: props.selectedModel,
                },
            };
            myChart.setOption(option);
        };
        // 点击图例
        const onLegendChange = (t) => {
            emit('legendChange', t);
        };
        // 根据 tooltip 的配置项显示提示框
        const showTip = ({ seriesIndex, dataIndex }) => {
            myChart?.dispatchAction({
                type: 'showTip',
                seriesIndex,
                dataIndex,
            });
        };
        // 隐藏 tooltip
        const hideTip = () => {
            myChart?.dispatchAction({
                type: 'hideTip',
            });
        };
        watch(() => [configProvider?.mode, props.quotationColor], () => {
            nextTick(() => updateColor());
        });
        provide(barlineChartContextKey, reactive({
            barlineChartElement: mainRef,
            wrapElement: wrapRef,
        }));
        onMounted(() => {
            init();
            resizeOb.value = new ResizeObserver(() => {
                handlerResize();
            });
            if (mainRef.value)
                resizeOb.value?.observe(mainRef.value);
        });
        onUnmounted(() => {
            if (myChart) {
                // 移除所有事件监听
                myChart.off('updateAxisPointer');
                myChart.getZr().off('globalout', handlerMouseOutView);
                myChart.off('mouseover');
                myChart.off('mouseout');
                myChart.clear();
                myChart.dispose();
                myChart = null;
            }
            if (resizeOb.value) {
                resizeOb.value.disconnect();
                resizeOb.value = null;
            }
            handlerMouseOutView.cancel();
            wrapRef.value = null;
            mainRef.value = null;
        });
        expose({
            /** @description update data */
            updateData,
            /** @description update color */
            updateColor,
            /** @description update selectedModel*/
            updateSelected,
            /** @description onRisize */
            handlerResize,
            /** @description echart init */
            init,
            /** @description Display prompt box */
            showTip,
            hideTip,
        });
        return () => {
            let XAXISDOM, LEGENDDOM;
            if (props.showCustomX) {
                XAXISDOM = (<XAxis left={props.grid?.length ? props.grid[0].left || 0 : props.left} right={props.grid?.length ? props.grid[0].right : props.right} xLabelList={props.chartData.x} is-show-tooltip-axis={props.tooltipAxisPointerType} show-custom-x-all={props.showCustomXAll} xLabelOverflow={props.xLabelOverflow}/>);
            }
            if (props.showCustomLegend && (props.legendList?.length || props.legend?.length)) {
                LEGENDDOM = (<Legend legendList={props.legendList?.length ? props.legendList : props.legend} isShowTotal={props.isShowTotal} selectedMode={props.selectedMode} isLabelRemoveId={props.isLabelRemoveId} colors={props.colors} scene={props.scene} spacing={props.spacing} onLegendChange={onLegendChange} v-slots={slots}/>);
            }
            return (<Spin spinning={props.loading}>
          <div>
            <div style={`height: ${props.height}`} class={`${prefixCls.value}`} ref={mainRef}></div>
            {XAXISDOM}
            {LEGENDDOM}
          </div>
        </Spin>);
        };
    },
});
export default BarLineChart;
